// src/admin/pages/MonitorListAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, orderBy, onSnapshot, doc } from "firebase/firestore";
import { db } from "../../firebase/firebase"; // パスはプロジェクト構成に合わせて
import "../styles/monitor.css";

export default function MonitorListAdmin() {
  // ランタイム（院内受付ON/待ち人数）
  const [inhouseOn, setInhouseOn] = useState(null);   // null=loading, true/false
  const [runtimeReady, setRuntimeReady] = useState(false);
  const [runtimeWaitCount, setRuntimeWaitCount] = useState(null);

  useEffect(() => {
    const ref = doc(db, "admin", "runtime");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() || {};
        setInhouseOn(d.inhouseOn === true);
        setRuntimeWaitCount(
          Number.isFinite(d.currentWaitCount) ? d.currentWaitCount : null
        );
        setRuntimeReady(true);
      },
      () => setRuntimeReady(true)
    );
    return () => unsub();
  }, []);

  // 受付ON時の一覧購読
  const [rows, setRows] = useState([]);   // [{id, no, hold}]
  const [rowsReady, setRowsReady] = useState(false);

  useEffect(() => {
    setRows([]);
    setRowsReady(false);
    if (inhouseOn !== true) return;

    // ※最初の利用時にコンソールから複合インデックス作成の案内が出る可能性があります
    const qref = query(
      collection(db, "reservation_slots"),
      where("isReserved", "==", true),
      where("canceled", "==", false),
      where("isReceptionAccepted", "==", false),
      where("canceledLineAt", "==", null),
      orderBy("reservationNumber", "asc")
    );

    const unsub = onSnapshot(
      qref,
      { includeMetadataChanges: true },
      (snap) => {
        const fromCache = snap.metadata.fromCache === true;
        if (snap.empty) {
          if (fromCache) return;
          setRows([]);
          setRowsReady(true);
          return;
        }
        const next = [];
        snap.forEach((docSnap) => {
          const d = docSnap.data();
          next.push({
            id: docSnap.id,
            no: d.reservationNumber,
            hold: d.hold === true,
          });
        });
        // 念のため昇順保証
        next.sort((a, b) => (a.no ?? 0) - (b.no ?? 0));
        setRows(next);
        setRowsReady(true);
      },
      (err) => {
        console.error("monitor list watch error:", err);
        setRows([]);
        setRowsReady(true);
      }
    );

    return () => unsub();
  }, [inhouseOn]);

  // 表示用：通常→保留の順に並べ替え、”あと◯人”を非保留だけでカウント
  const list = useMemo(() => {
    const normal = rows.filter((r) => r.hold !== true);
    const held   = rows.filter((r) => r.hold === true);
    const merged = [...normal, ...held];

    let rank = 0;
    return merged.map((r) => ({
      ...r,
      ahead: r.hold ? null : ++rank,
    }));
  }, [rows]);

  // ヘッダーの人数表示：runtime があれば優先、なければクライアント算出
  const headerWaitCount = useMemo(() => {
    if (Number.isFinite(runtimeWaitCount)) return runtimeWaitCount;
    return list.filter((r) => !r.hold).length || 0;
  }, [runtimeWaitCount, list]);

  // ---------- 描画分岐 ----------
  if (!runtimeReady || inhouseOn === null || (inhouseOn === true && !rowsReady)) {
    return (
      <div className="monitor-container">
        <div className="monitor-loading">読み込み中…</div>
      </div>
    );
  }

  if (inhouseOn === false) {
    return (
      <div className="monitor-container">
        <div className="monitor-closed">現在、院内受付は停止中です</div>
      </div>
    );
  }

  // ---------- 画面本体 ----------
  return (
    <div className="monitor-container">
      {/* ヘッダ：ただいま ◯ 人待ち */}
      <div className="whole-display">
        <div className="hito-size">ただいま</div>
        <div id="person" className="person-size">{headerWaitCount}</div>
        <div className="hito-size">人待ち</div>
      </div>

      {/* 見出し行（4カラム分） */}
      <div className="list-display">
        <div className="column-header">
          {[0,1,2,3].map((i) => (
            <div className="column-header-cell" key={`head-${i}`}>
              <div className="wait-no-header-whole">
                <div className="wait-no-header">番号</div>
              </div>
              <div className="last-person-header-whole">
                <div className="last-person-header">あと</div>
              </div>
            </div>
          ))}
        </div>

        {/* 本体グリッド（flex-wrap で横4カラムに折り返し） */}
        <div className="column">
          {list.map((r) => (
            <div className="column-cell" key={r.id}>
              {/* 左：番号（通常=緑 / 保留=黄） */}
              <div className={r.hold ? "wait-no-whole-horyu" : "wait-no-whole"}>
                <div className="wait-no">{r.no}</div>
              </div>
              {/* 右：あと（通常は人数 / 保留は「呼出不在」） */}
              {r.hold ? (
                <div className="last-person-whole-horyu">
                  <div className="last-person-horyu">呼出不在</div>
                </div>
              ) : (
                <div className="last-person-whole">

                    <div className="last-person-pair">
                        <div className="last-person">{r.ahead}</div>
                        <div className="last-person-moji">人</div>
                    </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
